import { isEqual } from "lodash"

export const getPathBAvailableMonths = (type, profiles, runningMonths, excludedProfile) => {
  const months = Array.isArray(runningMonths) ? runningMonths : Array.from({ length: 12 }, (_, index) => index + 1)
  const availableMonths = new Set(months)
  const baselineType = Number(type)
  if (![1, 2, 3].includes(baselineType)) return [...availableMonths].sort((left, right) => left - right)

  const profileList = Array.isArray(profiles) ? profiles : []
  profileList.forEach(profile => {
    if (profile === excludedProfile || (excludedProfile?.id != null && profile.id === excludedProfile.id)) return

    const otherType = Number(profile.info?.type)
    const conflicts = baselineType === 3 || otherType === 3 || baselineType === otherType
    if (!conflicts || !Array.isArray(profile.info?.months)) return

    profile.info.months.forEach(month => availableMonths.delete(month))
  })

  return [...availableMonths].sort((left, right) => left - right)
}

export const getPathBApplicableMonths = (profile, profiles, runningMonths) => {
  const allMonths = Array.isArray(runningMonths) ? runningMonths : Array.from({ length: 12 }, (_, index) => index + 1)
  const profileMonths = Array.isArray(profile?.info?.months) ? profile.info.months : allMonths
  const availableMonths = new Set(getPathBAvailableMonths(profile?.info?.type, profiles, runningMonths, profile))
  return profileMonths.filter(month => availableMonths.has(month))
}

export const getPathBProfilePeak = (profile, activeMonths) => {
  const runningHours = profile?.runningHours
  if (!runningHours || !Array.isArray(activeMonths)) return null

  const hasRunningHours = period => Array.isArray(runningHours[period]) && runningHours[period].some((hours, index) => activeMonths.includes(index + 1) && Number(hours) > 0)
  return Number(hasRunningHours("peak")) + Number(hasRunningHours("offPeak")) * 2
}

export const normalizePathAPeriods = pre => {
  const { profileCfg, profiles } = pre
  if (profileCfg?.estimatePath !== 1 || !profileCfg.enabledPeriods || !Array.isArray(profiles)) return pre

  const enabledPeriods = profileCfg.enabledPeriods
  const normalizedProfiles = profiles.map(profile => {
    const day = { 1: "weekday", 2: "weekend", 3: "wholeWeek" }[Number(profile.info?.type)]
    if (!day || !Array.isArray(profile.hourData)) return profile

    const allowed = enabledPeriods[day] ?? [1, 2]
    const hourData = profile.hourData.map(hour => {
      if (!Number(hour.period) || allowed.includes(Number(hour.period))) return hour
      return { ...hour, period: 0 }
    })
    const peakHours = hourData.filter(hour => Number(hour.period) === 1).length
    const offPeakHours = hourData.filter(hour => Number(hour.period) === 2).length
    const normalized = {
      ...profile,
      hourData,
      info: { ...profile.info, peak: Number(peakHours > 0) + Number(offPeakHours > 0) * 2 },
      runningHours: {
        ...profile.runningHours,
        peak: Array(12).fill(peakHours),
        offPeak: Array(12).fill(offPeakHours)
      }
    }
    return isEqual(profile, normalized) ? profile : normalized
  })

  const runningWeekPeaks = []
  normalizedProfiles.forEach(({ info }) => {
    const type = Number(info?.type)
    const peak = Number(info?.peak)
    const weeks = type === 3 ? [1, 2] : [1, 2].includes(type) ? [type] : []
    weeks.forEach(week => {
      const day = type === 3 ? "wholeWeek" : week === 1 ? "weekday" : "weekend"
      const allowed = enabledPeriods[day] ?? [1, 2]
      const periods = [1, 2]
      periods.forEach(period => {
        if ((peak === 3 || peak === period) && allowed.includes(period) && !runningWeekPeaks.some(pair => pair[0] === week && pair[1] === period)) {
          runningWeekPeaks.push([week, period])
        }
      })
    })
  })
  runningWeekPeaks.sort(([week1, period1], [week2, period2]) => week1 - week2 || period1 - period2)

  const profilesChanged = normalizedProfiles.some((profile, index) => profile !== profiles[index])
  if (!profilesChanged && isEqual(profileCfg.runningWeekPeaks, runningWeekPeaks)) return pre
  return { ...pre, profiles: profilesChanged ? normalizedProfiles : profiles, profileCfg: { ...profileCfg, runningWeekPeaks } }
}

export const normalizePathBPeriods = pre => {
  const { profileCfg, profiles } = pre
  if (profileCfg?.estimatePath !== 2 || !profileCfg.enabledPeriods || !Array.isArray(profiles)) return pre

  const enabledPeriods = profileCfg.enabledPeriods
  const normalizedProfiles = profiles.map(profile => {
    const type = Number(profile.info?.type)
    const day = { 1: "weekday", 2: "weekend", 3: "wholeWeek" }[type]
    const runningHours = profile.runningHours
    if (!day || !runningHours) return profile

    const allowed = enabledPeriods[day] ?? [1, 2]
    const normalizeHours = hours => {
      if (!Array.isArray(hours) || !hours.some(value => Number(value) !== 0)) return hours
      return hours.map(() => 0)
    }
    const nextRunningHours = { ...runningHours }
    if (!allowed.includes(1) && Array.isArray(runningHours.peak)) nextRunningHours.peak = normalizeHours(runningHours.peak)
    if (!allowed.includes(2) && Array.isArray(runningHours.offPeak)) nextRunningHours.offPeak = normalizeHours(runningHours.offPeak)
    const activeMonths = getPathBApplicableMonths(profile, profiles, profileCfg.runningMonths)
    const peakSummary = getPathBProfilePeak({ ...profile, runningHours: nextRunningHours }, activeMonths)
    const hasPeak = allowed.includes(1) && [1, 3].includes(peakSummary)
    const hasOffPeak = allowed.includes(2) && [2, 3].includes(peakSummary)
    const normalized = {
      ...profile,
      runningHours: nextRunningHours,
      info: { ...profile.info, peak: Number(hasPeak) + Number(hasOffPeak) * 2 }
    }
    return isEqual(profile, normalized) ? profile : normalized
  })

  const runningWeekPeaks = []
  normalizedProfiles.forEach(profile => {
    const type = Number(profile.info?.type)
    const peak = Number(profile.info?.peak)
    const weeks = type === 3 ? [1, 2] : [1, 2].includes(type) ? [type] : []
    weeks.forEach(week => {
      const day = type === 3 ? "wholeWeek" : week === 1 ? "weekday" : "weekend"
      const allowed = enabledPeriods[day] ?? [1, 2]
      const periods = [1, 2]
      periods.forEach(period => {
        if ((peak === 3 || peak === period) && allowed.includes(period) && !runningWeekPeaks.some(pair => pair[0] === week && pair[1] === period)) {
          runningWeekPeaks.push([week, period])
        }
      })
    })
  })
  runningWeekPeaks.sort(([week1, period1], [week2, period2]) => week1 - week2 || period1 - period2)

  const profilesChanged = normalizedProfiles.some((profile, index) => profile !== profiles[index])
  if (!profilesChanged && isEqual(profileCfg.runningWeekPeaks, runningWeekPeaks)) return pre
  return { ...pre, profiles: profilesChanged ? normalizedProfiles : profiles, profileCfg: { ...profileCfg, runningWeekPeaks } }
}



// WEBPACK FOOTER //
// ./src/views/mobile/wizard/commons/periods.js